// eslint-disable-next-line import/no-cycle
import router from '@/router';
import paymentIntent from '@/store/modules/payment/stripe/payment-intent';
import connectedAccount from '@/store/modules/payment/stripe/conneted-account';
import customer from '@/store/modules/payment/stripe/customer';
import subscription from '@/store/modules/payment/stripe/subscription';
import product from '@/store/modules/payment/stripe/product';
import price from '@/store/modules/payment/stripe/price';
import paymentRequestButton from '@/store/modules/payment/stripe/payment-request-button';
import { stripeConfig } from '@/config';

const loadStripeScript = () => import('@stripe/stripe-js').then(({ loadStripe }) => loadStripe);

const { publicKey } = stripeConfig;

export default {
  namespaced: true,
  modules: {
    paymentIntent,
    connectedAccount,
    customer,
    subscription,
    product,
    price,
    paymentRequestButton,
  },
  state: {
    stripe: null,
    style: {
      base: {
        iconColor: '#333',
        color: '#333',
        fontWeight: 400,
        fontFamily: "'Pulp Display', Lato, Helvetica, sans-serif",
        fontSize: '15px',
        '::placeholder': {
          color: '#bdbdbd',
        },
      },
      invalid: {
        iconColor: '#ed6b68',
        color: '#ed6b68',
      },
    },
    loading: false,
    confirmCardPaymentLoading: false,
    createCardTokenLoading: false,
  },
  mutations: {
    setStripe(state, stripe) {
      state.stripe = stripe;
    },
    setLoading(state, isLoading) {
      state.loading = isLoading;
    },
    setConfirmCardPaymentLoading(state, isLoading) {
      state.confirmCardPaymentLoading = isLoading;
    },
    setCreateCardTokenLoading(state, isLoading) {
      state.createCardTokenLoading = isLoading;
    },
  },
  actions: {
    async load({ commit, dispatch }, stripeAccount = null) {
      try {
        commit('setLoading', true);
        let stripe;
        if (stripeAccount) {
          stripe = await (await loadStripeScript())(publicKey, { stripeAccount });
        } else {
          stripe = await (await loadStripeScript())(publicKey);
        }
        commit('setStripe', stripe);
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
        throw e;
      } finally {
        commit('setLoading', false);
      }
    },
    async confirmCardPayment({ commit, getters, rootGetters, dispatch }, payload) {
      try {
        commit('setConfirmCardPaymentLoading', true);
        const { data, message, buttonName, productId } = payload;
        const cs = rootGetters['payment/stripe/paymentIntent/clientSecret'];

        const {
          error,
          paymentIntent: response,
        } = await getters.stripe.confirmCardPayment(cs, data);

        if (error) throw error;

        if (productId) {
          // eslint-disable-next-line no-restricted-globals
          history.pushState(null, '', `/payment/${productId}/success`);
        } else {
          await dispatch('redirectToSuccessPage', { message, buttonName });
        }

        return response;
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
        throw e;
      } finally {
        commit('setConfirmCardPaymentLoading', false);
      }
    },
    async createCardToken({ commit, getters, dispatch }, card) {
      try {
        commit('setCreateCardTokenLoading', true);

        const { error, token } = await getters.stripe.createToken(card);

        if (error) throw error;

        return token.id;
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
        throw e;
      } finally {
        commit('setCreateCardTokenLoading', false);
      }
    },
    redirectToSuccessPage(ctx, query = {}) {
      if (typeof query === 'string') {
        const message = query;
        query = {};

        query.message = message;
      } else {
        query.message = query?.message || '<h1 class="default-thank-you-message">Thanks for your purchase. Your support is appreciated!</h1>';
      }

      query.buttonName = query.buttonName || 'Go back';

      return router.push({ name: 'payment.success', query });
    },
  },
  getters: {
    stripe: state => state.stripe || {},
    style: state => state.style,
    loading: state => state.loading,
    confirmCardPaymentLoading: state => state.confirmCardPaymentLoading,
    createCardTokenLoading: state => state.createCardTokenLoading,
  },
};
